import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import type { TenantSiteEditorSectionId } from '../../../components/tenantSite/TenantSiteRenderer';
import LoadingSpinner from '../../../components/global/LoadingSpinner';
import { getCurrentBusinessConfig, patchPreferencesConfig } from '../../../firebase/firebase-delegate';
import {
  usePublishTenantSite,
  useTenantPublishStatus,
  useTenantSiteDraft,
  useUpdateTenantSiteDraft,
  useValidateTenantPublish,
  useChooseOnboardingSiteExperience,
} from '../../../sitePublishing/hooks';
import { createPublishingIdempotencyKey } from '../../../sitePublishing/api';
import {
  PublishValidationResult,
  TenantPublishingError,
} from '../../../sitePublishing/types';
import { useTenant } from '../../../tenancy/TenantContext';
import { BusinessConfig } from '../../../types';
import { registerUnsavedChanges } from '../../../utils/unsavedChangesGuard';
import PublishPanel from './PublishPanel';
import SiteDraftEditor from './SiteDraftEditor';
import SiteDraftPreview, { type PreviewViewport } from './SiteDraftPreview';
import {
  completeDraftSave,
  emptyDraftWorkspaceState,
  initializeDraftWorkspace,
  keepConflictingDraft,
  reloadDraftWorkspace,
  type SiteDraftUpdate,
  updateDraftWorkspace,
} from './draftWorkspaceState';
import {
  isTenantSiteEditorSectionId,
  SITE_EDITOR_SECTIONS,
  SITE_EDITOR_SECTION_MAP,
} from './siteEditorSections';
import {
  applySitePresentationSettings,
  normalizeSitePresentationSettings,
  presentationPreferencesPatch,
  SitePresentationSettings,
  sitePresentationSettingsEqual,
  sitePresentationSettingsFromConfig,
} from './sitePresentationSettings';

const LEGACY_SECTION_HASHES: Record<string, TenantSiteEditorSectionId> = {
  editor: 'hero',
  preview: 'hero',
  business: 'branding',
  gallery: 'gallery',
};

const SUPER_ADMIN_ONLY_SECTION_IDS = new Set<TenantSiteEditorSectionId>(['reviews']);
const BOOKING_ONLY_SECTION_IDS = new Set<TenantSiteEditorSectionId>([
  'style',
  'branding',
  'services',
  'booking',
]);

const PUBLISH_STAGE_MINIMUM_MS = {
  saving: 450,
  checking: 600,
  publishing: 750,
} as const;

const runForMinimumDuration = async <T,>(durationMs: number, task: () => Promise<T>): Promise<T> => {
  const startedAt = Date.now();
  try {
    return await task();
  } finally {
    const remainingMs = durationMs - (Date.now() - startedAt);
    if (remainingMs > 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, remainingMs));
    }
  }
};

type PresentationWorkspaceState = {
  saved: SitePresentationSettings;
  local: SitePresentationSettings;
};

const SitePublishingWorkspace: React.FC = () => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const statusQuery = useTenantPublishStatus();
  const draftQuery = useTenantSiteDraft();
  const businessConfigQuery = useQuery<BusinessConfig>({
    queryKey: ['businessConfig', tenantId],
    queryFn: () => getCurrentBusinessConfig(tenantId),
  });
  const updateMutation = useUpdateTenantSiteDraft();
  const validateMutation = useValidateTenantPublish();
  const publishMutation = usePublishTenantSite();
  const siteExperienceMutation = useChooseOnboardingSiteExperience();
  const [selectedSectionId, setSelectedSectionId] = useState<TenantSiteEditorSectionId>('style');
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState<number>();
  const [viewport, setViewport] = useState<PreviewViewport>('desktop');
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const [mobileRecommendationOpen, setMobileRecommendationOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [draftState, setDraftState] = useState(emptyDraftWorkspaceState);
  const [validation, setValidation] = useState<PublishValidationResult>();
  const [publishStage, setPublishStage] = useState<'idle' | 'saving' | 'checking' | 'publishing'>('idle');
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [lastPublishedAt, setLastPublishedAt] = useState<string>();
  const [publishFlowError, setPublishFlowError] = useState<string>();
  // Preview mode republishes on every save; this holds why the last one couldn't.
  const [previewIssues, setPreviewIssues] = useState<string[]>([]);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  const [presentationState, setPresentationState] = useState<PresentationWorkspaceState>();
  const { localDraft, draftVersion, hasUnsavedChanges, keptConflictCopy } = draftState;
  const presentationSettings = presentationState?.local;
  const hasUnsavedPresentationChanges = presentationState
    ? !sitePresentationSettingsEqual(presentationState.saved, presentationState.local)
    : false;
  const hasAnyUnsavedChanges = hasUnsavedChanges || hasUnsavedPresentationChanges;
  const isSuperAdminWorkspace = location.pathname.startsWith('/admin/superadmin/');
  const publishedDraftIsCurrent = statusQuery.data?.configSource === 'published' && (
    publishSuccess ||
    statusQuery.data.publishedDraftVersion === undefined ||
    statusQuery.data.publishedDraftVersion === draftVersion
  );
  const hasPublishedVersion = statusQuery.data?.configSource === 'published';
  const isPubliclyLive = hasPublishedVersion && statusQuery.data?.lifecycleState === 'active';
  const liveIsCurrent = isPubliclyLive && publishedDraftIsCurrent && !hasAnyUnsavedChanges;
  const hasPublishableChanges = !hasPublishedVersion || !publishedDraftIsCurrent || hasAnyUnsavedChanges;
  const liveUpdatedAt = lastPublishedAt ?? statusQuery.data?.publishedAt;
  const liveUpdatedLabel = liveUpdatedAt
    ? new Date(liveUpdatedAt).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    })
    : undefined;
  const liveStatusTooltip = !isPubliclyLive
    ? hasPublishedVersion ? 'Prepared, but not live yet.' : 'Not live yet.'
    : liveIsCurrent
      ? `Live${liveUpdatedLabel ? ` · Last updated ${liveUpdatedLabel}` : ''}`
      : `Live, but not up to date${liveUpdatedLabel ? ` · Last updated ${liveUpdatedLabel}` : ''}. Publish your latest changes to update it.`;

  const presentationMutation = useMutation({
    mutationFn: async (settings: SitePresentationSettings) => {
      const normalized = normalizeSitePresentationSettings(settings);
      await patchPreferencesConfig(
        presentationPreferencesPatch(normalized, businessConfigQuery.data?.preferences),
        tenantId
      );
      return normalized;
    },
    onSuccess: (saved) => {
      setPresentationState((current) => current
        ? { saved, local: current.local }
        : { saved, local: saved });
      queryClient.setQueryData<BusinessConfig>(['businessConfig', tenantId], (current) => current
        ? applySitePresentationSettings(current, saved)
        : current);
    },
  });
  const isSaving = updateMutation.isPending || presentationMutation.isPending;
  const isPublishingFlow = publishStage !== 'idle';
  const publishProgress = publishStage === 'saving' ? 25 : publishStage === 'checking' ? 60 : 90;
  const publishProgressLabel = publishStage === 'saving'
    ? 'Saving your changes…'
    : publishStage === 'checking'
      ? 'Checking everything…'
      : 'Publishing your website…';

  useEffect(() => {
    if (isMobile) setViewport('mobile');
  }, [isMobile]);

  useEffect(() => {
    setMobileRecommendationOpen(isMobile);
  }, [isMobile]);

  useEffect(() => {
    const requested = location.hash.replace(/^#/, '');
    if (requested === 'publish') {
      setPublishOpen(true);
      return;
    }
    const section = LEGACY_SECTION_HASHES[requested] ?? (isTenantSiteEditorSectionId(requested) ? requested : undefined);
    if (section) setSelectedSectionId(section);
  }, [location.hash]);

  useEffect(() => {
    if (!draftQuery.data) return;
    setDraftState((current) => initializeDraftWorkspace(current, draftQuery.data));
  }, [draftQuery.data, localDraft]);

  useEffect(() => {
    if (!businessConfigQuery.data) return;
    const incoming = sitePresentationSettingsFromConfig(businessConfigQuery.data);
    setPresentationState((current) => {
      if (!current || sitePresentationSettingsEqual(current.saved, current.local)) {
        return { saved: incoming, local: incoming };
      }
      return current;
    });
  }, [businessConfigQuery.data]);

  useEffect(() => {
    if (!hasAnyUnsavedChanges) return undefined;
    const message = 'You have unsaved website changes. Leave without saving?';
    const unregister = registerUnsavedChanges(message);
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      unregister();
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [hasAnyUnsavedChanges]);

  const canEdit = statusQuery.data?.viewer.isAdmin === true;
  const canPublish = statusQuery.data?.viewer.isAdmin === true;
  const validationIssues = useMemo(
    () => [...(validation?.errors ?? []), ...(validation?.warnings ?? [])],
    [validation]
  );
  // Reviews are curated by Scheduify, so only super admins see that section.
  const viewerIsSuperAdmin = statusQuery.data?.viewer.isSuperAdmin === true;
  const inSalesPreview = statusQuery.data?.salesPreview?.enabled === true && statusQuery.data.salesPreview.available;
  const visibleEditorSections = useMemo(
    () => (localDraft?.experience === 'booking_only'
      ? SITE_EDITOR_SECTIONS
        .filter((section) => BOOKING_ONLY_SECTION_IDS.has(section.id))
        .map((section) => section.id === 'branding' ? { ...section, label: 'Brand' } : section)
      : SITE_EDITOR_SECTIONS
    ).filter((section) => viewerIsSuperAdmin || !SUPER_ADMIN_ONLY_SECTION_IDS.has(section.id)),
    [localDraft?.experience, viewerIsSuperAdmin]
  );
  const selectedSection = visibleEditorSections.find((section) => section.id === selectedSectionId) ??
    SITE_EDITOR_SECTION_MAP[selectedSectionId];
  const selectedSectionIndex = visibleEditorSections.findIndex((section) => section.id === selectedSectionId);
  const sectionPathPrefixes: Record<TenantSiteEditorSectionId, string[]> = {
    style: ['theme'], branding: ['business.displayName', 'assets.logo'],
    hero: ['homepage.hero', 'homepage.heroMediaType', 'assets.hero', 'assets.heroVideo'], services: ['booking.showPrices'],
    gallery: ['homepage.gallery'], team: ['sections.showTeam'], reviews: ['homepage.reviews'],
    about: ['homepage.about'], faq: ['homepage.faq', 'sections.showFaq'],
    hours: ['business.businessHours'], booking: ['booking'],
    contact: ['business.address', 'business.phone', 'business.email', 'business.googlePlaceId', 'socials'],
    seo: ['metadata'],
  };
  const selectedSectionHasErrors = (validation?.errors ?? []).some((issue) =>
    sectionPathPrefixes[selectedSectionId].some((prefix) => issue.path === prefix || issue.path?.startsWith(`${prefix}.`)));

  const updateLocalDraft = (update: SiteDraftUpdate) => {
    setDraftState((current) => updateDraftWorkspace(current, update));
    setValidation(undefined);
    setPublishSuccess(false);
  };

  const updatePresentationSettings = (settings: SitePresentationSettings) => {
    setPresentationState((current) => current ? { ...current, local: settings } : current);
    setPublishSuccess(false);
  };

  const selectSection = (sectionId: TenantSiteEditorSectionId, openMobileEditor = true) => {
    if (!isTenantSiteEditorSectionId(sectionId)) return;
    if (!viewerIsSuperAdmin && SUPER_ADMIN_ONLY_SECTION_IDS.has(sectionId)) return;
    setSelectedSectionId(sectionId);
    if (isMobile && openMobileEditor) setMobileEditorOpen(true);
  };

  useEffect(() => {
    const bookingOnlyMismatch = localDraft?.experience === 'booking_only' && !BOOKING_ONLY_SECTION_IDS.has(selectedSectionId);
    const hiddenFromViewer = Boolean(statusQuery.data) && !viewerIsSuperAdmin && SUPER_ADMIN_ONLY_SECTION_IDS.has(selectedSectionId);
    if (bookingOnlyMismatch || hiddenFromViewer) setSelectedSectionId('style');
  }, [localDraft?.experience, selectedSectionId, viewerIsSuperAdmin, statusQuery.data]);

  const saveDraft = async (): Promise<number> => {
    if (!localDraft || draftVersion === undefined || !draftQuery.data || !presentationSettings) {
      throw new Error('The website editor is not ready yet.');
    }

    let savedDraftVersion = draftVersion;
    const draftSave = hasUnsavedChanges
      ? updateMutation.mutateAsync({
        expectedDraftVersion: draftVersion,
        schemaVersion: draftQuery.data.schemaVersion,
        content: localDraft,
      }).then((response) => {
        savedDraftVersion = response.draftVersion;
        setPreviewIssues(response.preview && !response.preview.published ? response.preview.issues : []);
        if (response.preview?.published) void statusQuery.refetch();
        setDraftState((current) => completeDraftSave(current, response));
        void draftQuery.refetch();
      })
      : Promise.resolve();
    const presentationSave = hasUnsavedPresentationChanges
      ? presentationMutation.mutateAsync(presentationSettings).then(() => undefined)
      : Promise.resolve();

    try {
      await Promise.all([draftSave, presentationSave]);
      return savedDraftVersion;
    } catch (error) {
      if (error instanceof TenantPublishingError && error.code === 'DRAFT_VERSION_CONFLICT') {
        setConflictOpen(true);
      }
      throw error;
    }
  };

  const publishChanges = async () => {
    if (!canPublish || !hasPublishableChanges || isPublishingFlow || isSaving) return;
    setPublishFlowError(undefined);
    setPublishSuccess(false);
    setValidation(undefined);

    try {
      setPublishStage('saving');
      const savedDraftVersion = await runForMinimumDuration(
        PUBLISH_STAGE_MINIMUM_MS.saving,
        saveDraft
      );
      setPublishStage('checking');
      const result = await runForMinimumDuration(
        PUBLISH_STAGE_MINIMUM_MS.checking,
        () => validateMutation.mutateAsync(savedDraftVersion)
      );
      setValidation(result);
      if (!result.valid) {
        setPublishOpen(true);
        return;
      }

      setPublishStage('publishing');
      const published = await runForMinimumDuration(PUBLISH_STAGE_MINIMUM_MS.publishing, () =>
        publishMutation.mutateAsync({
          expectedDraftVersion: savedDraftVersion,
          idempotencyKey: createPublishingIdempotencyKey(),
          confirmOnboardingSetup: statusQuery.data?.lifecycleState === 'onboarding',
        })
      );
      setLastPublishedAt(published.publishedAt);
      setPublishOpen(false);
      setPublishSuccess(true);
      void statusQuery.refetch();
    } catch (error) {
      setPublishFlowError(error instanceof Error ? error.message : 'The website could not be published.');
    } finally {
      setPublishStage('idle');
    }
  };

  const saveDraftOnly = async () => {
    if (isSaving || isPublishingFlow) return;
    setPublishFlowError(undefined);
    try {
      await saveDraft();
    } catch (error) {
      setPublishFlowError(error instanceof Error ? error.message : 'The draft could not be saved.');
    }
  };

  const reloadServerVersion = async () => {
    const result = await draftQuery.refetch();
    if (result.data) {
      setDraftState(reloadDraftWorkspace(result.data));
      setValidation(undefined);
      setConflictOpen(false);
    }
  };

  const revertDraft = () => {
    if (draftQuery.data) setDraftState(reloadDraftWorkspace(draftQuery.data));
    setPresentationState((current) => current ? {saved: current.saved, local: current.saved} : current);
    setValidation(undefined);
    setRevertConfirmOpen(false);
  };

  if (statusQuery.isLoading || draftQuery.isLoading || businessConfigQuery.isLoading) return <LoadingSpinner label="Loading your site" />;
  if (statusQuery.isError) {
    const forbidden = statusQuery.error instanceof TenantPublishingError && statusQuery.error.code === 'FORBIDDEN';
    return <Alert severity={forbidden ? 'warning' : 'error'}>{forbidden
      ? 'You do not have active site.read access for this tenant.'
      : statusQuery.error.message}</Alert>;
  }
  if (!statusQuery.data) return <Alert severity="error">Publishing status is unavailable.</Alert>;
  if (draftQuery.isError) {
    const missing = draftQuery.error instanceof TenantPublishingError && draftQuery.error.code === 'NOT_FOUND';
    return <Alert severity={missing ? 'info' : 'error'}>{missing
      ? 'Website editing has not been initialized for this business yet.'
      : draftQuery.error.message}</Alert>;
  }
  if (businessConfigQuery.isError || !businessConfigQuery.data) {
    return <Alert severity="error">Site presentation settings are unavailable.</Alert>;
  }
  if (!localDraft || draftVersion === undefined || !presentationSettings) {
    return <Alert severity="error">The website editor could not be initialized.</Alert>;
  }

  const editor = (
    <SiteDraftEditor
      draft={localDraft}
      disabled={!canEdit || isSaving}
      issues={validationIssues}
      sectionId={selectedSectionId}
      presentationSettings={presentationSettings}
      onChange={updateLocalDraft}
      onPresentationSettingsChange={updatePresentationSettings}
      selectedGalleryIndex={selectedGalleryIndex}
    />
  );

  return (
    <Stack spacing={2} sx={{ width: '100%', maxWidth: 1580, mx: 'auto', pb: { xs: 3, md: 20 } }}>
      {isSuperAdminWorkspace ? (
        <Alert
          severity="info"
          action={(
            <Button
              color="inherit"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/admin/superadmin')}
            >
              Tools
            </Button>
          )}
        >
          <strong>{localDraft.business.displayName}</strong> ({statusQuery.data.tenantId})
        </Alert>
      ) : null}

      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        justifyContent="flex-end"
        sx={{ display: { xs: 'flex', md: 'none' } }}
      >
          {statusQuery.data.lifecycleState === 'onboarding' ? <Button
            sx={{ mr: 'auto' }}
            onClick={() => setSkipConfirmOpen(true)}
            disabled={siteExperienceMutation.isPending}
          >Skip for now</Button> : null}
          <Tooltip title={liveStatusTooltip}>
            <Chip
              size="small"
              variant="filled"
              color={liveIsCurrent ? 'success' : 'default'}
              label="Live"
              aria-label={liveStatusTooltip}
              sx={!isPubliclyLive ? {
                bgcolor: 'action.disabledBackground',
                color: 'text.disabled',
              } : !liveIsCurrent ? {
                bgcolor: 'common.black',
                color: 'common.white',
                '&:hover': { bgcolor: 'grey.900' },
              } : undefined}
            />
          </Tooltip>
          {statusQuery.data.publicUrl ? (
            <Tooltip title="Open live website">
              <IconButton
                component="a"
                href={statusQuery.data.publicUrl}
                target="_blank"
                rel="noreferrer"
                size="small"
                aria-label="Open live website"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Save a draft without publishing">
            <span>
              <IconButton
                aria-label="Save draft"
                onClick={() => void saveDraftOnly()}
                disabled={!canEdit || !hasAnyUnsavedChanges || isSaving || isPublishingFlow}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Discard unpublished changes">
            <span>
              <IconButton
                aria-label="Discard unpublished changes"
                color="error"
                onClick={() => setRevertConfirmOpen(true)}
                disabled={!hasAnyUnsavedChanges || isSaving || isPublishingFlow}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PublishOutlinedIcon />}
            onClick={() => void publishChanges()}
            disabled={!canPublish || !hasPublishableChanges || isPublishingFlow || isSaving}
          >
            {publishStage === 'saving' ? 'Saving…'
              : publishStage === 'checking' ? 'Checking…'
                : publishStage === 'publishing' ? 'Publishing…'
                  : statusQuery.data.lifecycleState === 'onboarding' ? 'Save & finish' : 'Publish changes'}
          </Button>
      </Stack>

      {keptConflictCopy ? (
        <Alert severity="warning">This local copy is stale. Load the saved website before saving again.</Alert>
      ) : null}
      {updateMutation.isError && !(
        updateMutation.error instanceof TenantPublishingError &&
        updateMutation.error.code === 'DRAFT_VERSION_CONFLICT'
      ) ? <Alert severity="error">{updateMutation.error.message}</Alert> : null}
      {presentationMutation.isError ? (
        <Alert severity="error">{presentationMutation.error.message}</Alert>
      ) : null}
      {isPublishingFlow ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }} aria-live="polite">
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight={800}>{publishProgressLabel}</Typography>
              <Typography variant="caption" color="text.secondary">{publishProgress}%</Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={publishProgress}
              aria-label={publishProgressLabel}
              sx={{ height: 8, borderRadius: 999 }}
            />
          </Stack>
        </Paper>
      ) : null}
      {publishFlowError ? <Alert severity="error">{publishFlowError}</Alert> : null}
      {inSalesPreview ? <Alert severity={previewIssues.length ? 'warning' : 'info'}>
        {previewIssues.length ?
          `Saved, but the preview site wasn't updated: ${previewIssues.join(' ')}` :
          'Preview mode is on: saving updates the public preview site right away.'}
      </Alert> : null}
      {publishSuccess ? <Alert severity="success">
        {statusQuery.data.lifecycleState === 'onboarding'
          ? 'Website setup saved. You can continue to launch.'
          : 'Your website changes are live.'}
      </Alert> : null}
      {!canEdit ? <Alert severity="info">This website is read-only for your account.</Alert> : null}
      {localDraft.experience === 'booking_only' ? (
        <Alert severity="info" action={(
          <Button color="inherit" size="small" disabled={!canEdit || isSaving}
            onClick={() => updateLocalDraft((draft) => ({ ...draft, experience: 'full_site' }))}>
            Build full website
          </Button>
        )}>
          Your public site currently opens directly to booking. You can build the complete website at any time.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, display: { xs: 'block', md: 'none' } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography fontWeight={800}>Site mode</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose whether visitors see the complete website or go straight to booking.
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={localDraft.experience}
            onChange={(_, experience: 'full_site' | 'booking_only' | null) => {
              if (experience) updateLocalDraft((draft) => ({ ...draft, experience }));
            }}
            disabled={!canEdit || isSaving}
            aria-label="Public site mode"
            sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, '& .MuiToggleButton-root': { flex: 1, px: 2 } }}
          >
            <ToggleButton value="full_site">Full website</ToggleButton>
            <ToggleButton value="booking_only">Booking only</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, overflow: 'hidden', display: 'flex', alignItems: 'center' }}
      >
        <IconButton
          aria-label="Previous website section"
          disabled={selectedSectionIndex <= 0}
          onClick={() => selectSection(visibleEditorSections[selectedSectionIndex - 1].id)}
        ><ChevronLeftIcon /></IconButton>
        <Tabs
          value={selectedSectionId}
          onChange={(_, value: TenantSiteEditorSectionId) => selectSection(value)}
          variant="scrollable"
          scrollButtons={false}
          aria-label="Website sections"
          sx={{ minWidth: 0, flex: 1, minHeight: 52, '& .MuiTab-root': { minWidth: 128, minHeight: 52, textTransform: 'none', fontWeight: 700 } }}
        >
          {visibleEditorSections.map((section) => <Tab key={section.id} value={section.id} label={section.label} />)}
        </Tabs>
        <IconButton
          aria-label="Next website section"
          disabled={selectedSectionIndex >= visibleEditorSections.length - 1 || selectedSectionHasErrors}
          onClick={() => selectSection(visibleEditorSections[selectedSectionIndex + 1].id)}
        ><ChevronRightIcon /></IconButton>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1, md: 1.5 },
          borderRadius: 4,
          bgcolor: '#f3f5f3',
          overflow: 'hidden',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5, pb: 1.25 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            LIVE DRAFT
          </Typography>
          <ButtonGroup size="small" aria-label="Preview viewport" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
            <Button
              aria-label="Desktop preview"
              variant={viewport === 'desktop' ? 'contained' : 'outlined'}
              onClick={() => setViewport('desktop')}
              startIcon={<DesktopWindowsOutlinedIcon />}
            >
              Desktop
            </Button>
            <Button
              aria-label="Mobile preview"
              variant={viewport === 'mobile' ? 'contained' : 'outlined'}
              onClick={() => setViewport('mobile')}
              startIcon={<PhoneIphoneOutlinedIcon />}
            >
              Mobile
            </Button>
          </ButtonGroup>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 390px' },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          <SiteDraftPreview
            draft={localDraft}
            presentationSettings={presentationSettings}
            viewport={viewport}
            onViewportChange={setViewport}
            selectedSectionId={selectedSectionId}
            onSelectSection={selectSection}
            selectedGalleryIndex={selectedGalleryIndex}
            onSelectGalleryImage={setSelectedGalleryIndex}
            showControls={false}
            comingSoon={inSalesPreview}
          />
          {!isMobile ? (
            <Paper
              elevation={0}
              sx={{
                maxHeight: 'calc(100vh - 220px)',
                minHeight: 480,
                p: 2.5,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper',
              }}
            >
              {editor}
            </Paper>
          ) : null}
        </Box>
      </Paper>

      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: 1200,
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1,
          pointerEvents: 'none',
        }}
        aria-label="Site builder controls"
      >
        <Paper
          elevation={10}
          sx={{ p: 0.5, borderRadius: 999, pointerEvents: 'auto', border: '1px solid', borderColor: 'divider' }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={localDraft.experience}
            onChange={(_, experience: 'full_site' | 'booking_only' | null) => {
              if (experience) updateLocalDraft((draft) => ({ ...draft, experience }));
            }}
            disabled={!canEdit || isSaving || isPublishingFlow}
            aria-label="Public site mode"
            sx={{
              '& .MuiToggleButton-root': {
                border: 0,
                borderRadius: '999px !important',
                px: 2,
                textTransform: 'none',
                fontWeight: 800,
              },
            }}
          >
            <ToggleButton value="full_site">Full website</ToggleButton>
            <ToggleButton value="booking_only">Booking only</ToggleButton>
          </ToggleButtonGroup>
        </Paper>

        <Paper
          elevation={12}
          sx={{
            p: 0.75,
            pl: 1,
            borderRadius: 999,
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {statusQuery.data.lifecycleState === 'onboarding' ? (
            <Button
              size="small"
              color="inherit"
              onClick={() => setSkipConfirmOpen(true)}
              disabled={siteExperienceMutation.isPending}
              sx={{ borderRadius: 999, textTransform: 'none' }}
            >
              Skip for now
            </Button>
          ) : null}
          <Tooltip title={liveStatusTooltip}>
            <Chip
              size="small"
              variant="filled"
              color={liveIsCurrent ? 'success' : 'default'}
              label="Live"
              aria-label={liveStatusTooltip}
              sx={!isPubliclyLive ? {
                bgcolor: 'action.disabledBackground',
                color: 'text.disabled',
              } : !liveIsCurrent ? {
                bgcolor: 'common.black',
                color: 'common.white',
              } : undefined}
            />
          </Tooltip>
          {statusQuery.data.publicUrl ? (
            <Tooltip title="Open live website">
              <IconButton
                component="a"
                href={statusQuery.data.publicUrl}
                target="_blank"
                rel="noreferrer"
                size="small"
                aria-label="Open live website"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Save a draft without publishing">
            <span>
              <IconButton
                size="small"
                aria-label="Save draft"
                onClick={() => void saveDraftOnly()}
                disabled={!canEdit || !hasAnyUnsavedChanges || isSaving || isPublishingFlow}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Discard unpublished changes">
            <span>
              <IconButton
                size="small"
                aria-label="Discard unpublished changes"
                onClick={() => setRevertConfirmOpen(true)}
                disabled={!hasAnyUnsavedChanges || isSaving || isPublishingFlow}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<PublishOutlinedIcon />}
            onClick={() => void publishChanges()}
            disabled={!canPublish || !hasPublishableChanges || isPublishingFlow || isSaving}
            sx={{ borderRadius: 999, px: 2, textTransform: 'none', fontWeight: 800 }}
          >
            {publishStage === 'saving' ? 'Saving…'
              : publishStage === 'checking' ? 'Checking…'
                : publishStage === 'publishing' ? 'Publishing…'
                  : statusQuery.data.lifecycleState === 'onboarding' ? 'Save & finish' : 'Publish changes'}
          </Button>
        </Paper>
      </Box>

      <Paper
        elevation={8}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'sticky',
          bottom: 12,
          zIndex: 20,
          p: 1,
          alignItems: 'center',
          gap: 1,
          borderRadius: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, pl: 1 }}>
          <Typography variant="caption" color="text.secondary">Editing</Typography>
          <Typography variant="body2" fontWeight={800}>{selectedSection.label}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditOutlinedIcon />}
          onClick={() => setMobileEditorOpen(true)}
        >
          Edit
        </Button>
      </Paper>

      <Drawer
        anchor="bottom"
        open={mobileEditorOpen}
        onClose={() => setMobileEditorOpen(false)}
        PaperProps={{ sx: { maxHeight: '86vh', borderRadius: '20px 20px 0 0' } }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" color="text.secondary">Site Builder</Typography>
            <Typography variant="subtitle1" fontWeight={800}>{selectedSection.label}</Typography>
          </Box>
          <IconButton aria-label="Close editor" onClick={() => setMobileEditorOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ p: 2.5, overflowY: 'auto' }}>{editor}</Box>
      </Drawer>

      <Dialog
        open={mobileRecommendationOpen}
        onClose={() => setMobileRecommendationOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="mobile-builder-recommendation-title"
      >
        <DialogContent sx={{ pt: 4, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '50%',
              bgcolor: 'action.hover',
            }}
          >
            <DesktopWindowsOutlinedIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography id="mobile-builder-recommendation-title" variant="h5" fontWeight={800} gutterBottom>
            Build better on a bigger screen
          </Typography>
          <Typography color="text.secondary">
            The Site Builder works on mobile, but a tablet or computer gives you more room to preview your site and edit every section comfortably.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => setMobileRecommendationOpen(false)}
          >
            Continue on mobile
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        PaperProps={{
          sx: isMobile
            ? { maxHeight: '92vh', borderRadius: '20px 20px 0 0' }
            : { width: 480, maxWidth: '100vw' },
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={800}>
              {validation?.valid === false ? 'Fix before publishing' : 'Publish changes'}
            </Typography>
          </Box>
          <IconButton aria-label="Close publish review" onClick={() => setPublishOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ p: 3, overflowY: 'auto' }}>
          <PublishPanel
            validation={validation}
            isPublishing={isPublishingFlow}
            onRetry={() => void publishChanges()}
            onNavigateToSection={(sectionId) => {
              setPublishOpen(false);
              window.setTimeout(() => selectSection(sectionId), 180);
            }}
          />
        </Box>
      </Drawer>

      <Dialog open={revertConfirmOpen} onClose={() => setRevertConfirmOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent><Alert severity="warning">This permanently restores the last saved website and presentation settings.</Alert></DialogContent>
        <DialogActions><Button onClick={() => setRevertConfirmOpen(false)}>Cancel</Button><Button color="error" variant="contained" onClick={revertDraft}>Discard changes</Button></DialogActions>
      </Dialog>

      <Dialog open={skipConfirmOpen} onClose={() => setSkipConfirmOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Use a booking-only site?</DialogTitle>
        <DialogContent><Alert severity={hasAnyUnsavedChanges ? 'warning' : 'info'}>
          {hasAnyUnsavedChanges ? 'Your unsaved website edits will not be included. ' : ''}
          Customers will see a focused booking experience. You can build the full website later.
        </Alert></DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => siteExperienceMutation.mutate('booking_only', {
            onSuccess: () => setSkipConfirmOpen(false),
          })} disabled={siteExperienceMutation.isPending}>Use booking only</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={conflictOpen} onClose={() => setConflictOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Someone else saved website changes</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography>The saved website changed after you opened the editor.</Typography>
            <Alert severity="warning">Your local changes are still here. Gallery changes cannot be merged automatically.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          <Button onClick={() => { setDraftState(keepConflictingDraft); setConflictOpen(false); }}>
            Keep local copy
          </Button>
          <Button variant="contained" onClick={() => void reloadServerVersion()}>Load saved website</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SitePublishingWorkspace;
